import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';
import { Heart, MessageCircle, Globe, Users, Send, Loader2, MoreHorizontal, Flag, Pencil, Trash2, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { postsApi, mediaUrl } from '../services/postsApi';
import MentionText from './community/MentionText';
import CommentComposer from './community/CommentComposer';
import OfficialBadge from './community/OfficialBadge';
import ShareVerseButton from './community/ShareVerseButton';
import { announceFollow } from './community/SuggestedForYou';

const REPORT_REASONS = [
  { id: 'spam', en: 'Spam', es: 'Spam' },
  { id: 'inappropriate', en: 'Inappropriate content', es: 'Contenido inapropiado' },
  { id: 'harassment', en: 'Harassment or bullying', es: 'Acoso' },
  { id: 'other', en: 'Something else', es: 'Otra cosa' },
];

// Opens in YouTube (app on phones): many church livestream replays block playback inside other sites.
const PostVideo = ({ id, thumb, title, postId, es }) => (
  <a href={`https://www.youtube.com/watch?v=${id}`} target="_blank" rel="noopener noreferrer"
    className="group relative block w-full aspect-video bg-black" data-testid={`post-video-play-${postId}`}>
    <img src={thumb} alt={title || 'video'} referrerPolicy="no-referrer" loading="lazy" className="w-full h-full object-cover" />
    <span className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/40 transition-colors">
      <PlayCircle className="w-16 h-16 text-white drop-shadow-lg transition-transform group-hover:scale-110" />
    </span>
    <span className="absolute bottom-2 right-2 rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-medium text-white">
      {es ? 'Ver en YouTube' : 'Watch on YouTube'}
    </span>
  </a>
);

const PostCard = ({ post, language = 'en', onOpenProfile, currentUserId, onDeleted, onUpdated, isGuest, requireAuth }) => {
  const es = language === 'es';
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likes, setLikes] = useState(post.likes_count);
  const [caption, setCaption] = useState(post.caption || '');
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('spam');
  const [reportNote, setReportNote] = useState('');
  const [reporting, setReporting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editText, setEditText] = useState(post.caption || '');
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isOwn = post.user_id === currentUserId;

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await postsApi.update(post.id, editText);
      const newCaption = res?.post?.caption ?? editText.trim();
      setCaption(newCaption);
      onUpdated?.(post.id, newCaption);
      setEditOpen(false);
      toast.success(es ? 'Publicación actualizada' : 'Post updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'No se pudo editar' : 'Could not edit post'));
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await postsApi.remove(post.id);
      onDeleted?.(post.id);
      toast.success(es ? 'Publicación eliminada' : 'Post deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'No se pudo eliminar' : 'Could not delete post'));
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const submitReport = async () => {
    setReporting(true);
    try {
      const res = await postsApi.report(post.id, reportReason, reportNote);
      toast.success(res.already_reported
        ? (es ? 'Ya reportaste esta publicación' : 'You already reported this post')
        : (es ? 'Gracias, lo revisaremos' : 'Thanks — we\'ll review it'));
      setReportOpen(false); setReportNote(''); setReportReason('spam');
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'No se pudo reportar' : 'Could not report'));
    } finally { setReporting(false); }
  };

  // Guests get the sign-in prompt instead of a failed request.
  const needsSignIn = (label, action) => {
    if (!isGuest && currentUserId) return false;
    requireAuth?.(label, action);
    return true;
  };

  const [followingAuthor, setFollowingAuthor] = useState(false);
  useEffect(() => {
    const sync = (e) => { if (e.detail.uid === post.user_id) setFollowingAuthor(e.detail.on); };
    window.addEventListener('lumera:follow-changed', sync);
    return () => window.removeEventListener('lumera:follow-changed', sync);
  }, [post.user_id]);
  const followAuthor = async () => {
    if (needsSignIn(es ? 'Seguir personas' : 'Following people', { type: 'follow_user', target: post.user_id })) return;
    announceFollow(post.user_id, true);
    try { await postsApi.follow(post.user_id); } catch { announceFollow(post.user_id, false); }
  };

  const toggleLike = async () => {
    if (needsSignIn(es ? 'Dar me gusta' : 'Liking posts', { type: 'like_post', target: post.id })) return;
    const next = !liked;
    setLiked(next); setLikes((n) => n + (next ? 1 : -1));
    try {
      if (next) await postsApi.like(post.id); else await postsApi.unlike(post.id);
    } catch {
      setLiked(!next); setLikes((n) => n + (next ? -1 : 1));
      toast.error(es ? 'Error' : 'Something went wrong');
    }
  };

  const openComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && comments === null) {
      try { setComments(await postsApi.comments(post.id)); } catch { setComments([]); }
    }
  };

  const submitCommentText = async (text) => {
    if (needsSignIn(es ? 'Comentar' : 'Commenting', { type: 'comment_post', target: post.id, text })) throw new Error('auth_required');
    try {
      const res = await postsApi.addComment(post.id, text);
      setComments((c) => [...(c || []), res.comment]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'No se pudo comentar' : 'Could not comment'));
      throw e;
    }
  };

  const openMention = async (handle) => {
    try {
      const u = await postsApi.resolveUsername(handle);
      if (u?.user_id) onOpenProfile?.(u.user_id);
    } catch {
      toast.error(es ? 'Usuario no encontrado' : 'User not found');
    }
  };

  const initials = (post.author_name || '?').slice(0, 2).toUpperCase();

  return (
    <Card className="overflow-hidden" data-testid={`post-card-${post.id}`}>
      <div className="flex items-center gap-3 p-3">
        <button onClick={() => onOpenProfile?.(post.user_id)} className="flex items-center gap-3" data-testid={`post-author-${post.id}`}>
          <Avatar className="w-9 h-9">
            {post.author_picture ? <AvatarImage src={post.author_picture} /> : null}
            <AvatarFallback className="text-xs bg-primary/15">{initials}</AvatarFallback>
          </Avatar>
          <div className="text-left">
            {post.suggested && (
              <p className="text-[10px] font-medium uppercase tracking-wide text-primary" data-testid={`post-suggested-${post.id}`}>
                {es ? 'Sugerido para ti' : 'Suggested for you'}
              </p>
            )}
            <p className="text-sm font-semibold leading-tight flex items-center gap-1">
              {post.author_name}
              {post.author_is_official && <OfficialBadge className="w-3.5 h-3.5" />}
            </p>
            {post.author_username && <p className="text-[11px] text-muted-foreground">@{post.author_username}</p>}
            {post.verse_reference && <p className="text-[11px] text-muted-foreground">{post.verse_reference}</p>}
          </div>
        </button>
        {post.suggested && !isOwn && (
          <button type="button" onClick={followAuthor} disabled={followingAuthor}
            className={`ml-auto text-xs font-semibold ${followingAuthor ? 'text-muted-foreground' : 'text-primary hover:underline'}`}
            data-testid={`post-follow-author-${post.id}`}>
            {followingAuthor ? (es ? 'Siguiendo' : 'Following') : (es ? 'Seguir' : 'Follow')}
          </button>
        )}
        <span className={`${post.suggested && !isOwn ? 'ml-2' : 'ml-auto'} text-muted-foreground`} title={post.visibility}>
          {post.visibility === 'friends' ? <Users className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
        </span>
        {isOwn && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-muted-foreground hover:text-foreground p-1 -mr-1" data-testid={`post-menu-${post.id}`} aria-label={es ? 'Opciones' : 'Options'}>
                <MoreHorizontal className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => { setEditText(caption); setEditOpen(true); }} data-testid={`post-edit-${post.id}`}>
                <Pencil className="w-4 h-4 mr-2" /> {es ? 'Editar' : 'Edit'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDeleteOpen(true)} className="text-rose-600 focus:text-rose-600" data-testid={`post-delete-${post.id}`}>
                <Trash2 className="w-4 h-4 mr-2" /> {es ? 'Eliminar' : 'Delete'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {!isOwn && currentUserId && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-muted-foreground hover:text-foreground p-1 -mr-1" data-testid={`post-menu-${post.id}`} aria-label={es ? 'Opciones' : 'Options'}>
                <MoreHorizontal className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setReportOpen(true)} className="text-rose-600 focus:text-rose-600" data-testid={`post-report-${post.id}`}>
                <Flag className="w-4 h-4 mr-2" /> {es ? 'Reportar' : 'Report'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {post.video_youtube_id
        ? <PostVideo id={post.video_youtube_id} thumb={post.image_url} title={caption} postId={post.id} es={es} />
        : <img src={mediaUrl(post.image_url)} alt={caption || 'post'} className="w-full aspect-square object-cover bg-muted" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}

      <div className="p-3 space-y-2">
        <div className="flex items-center gap-4">
          <button onClick={toggleLike} className="flex items-center gap-1.5" data-testid={`post-like-${post.id}`}>
            <Heart className={`w-6 h-6 transition-colors ${liked ? 'fill-rose-500 text-rose-500' : 'text-foreground'}`} />
          </button>
          <button onClick={openComments} className="flex items-center gap-1.5" data-testid={`post-comment-toggle-${post.id}`}>
            <MessageCircle className="w-6 h-6" />
          </button>
          {post.is_official_daily && <ShareVerseButton post={post} es={es} />}
        </div>
        {likes > 0 && <p className="text-sm font-semibold" data-testid={`post-likes-${post.id}`}>{likes} {es ? 'me gusta' : (likes === 1 ? 'like' : 'likes')}</p>}
        {caption && (
          <p className="text-sm whitespace-pre-line"><span className="font-semibold mr-1.5">{post.author_name}</span><MentionText text={caption} onMention={openMention} /></p>
        )}
        {post.comments_count > 0 && !showComments && (
          <button onClick={openComments} className="text-xs text-muted-foreground" data-testid={`post-view-comments-${post.id}`}>
            {es
              ? `Ver ${post.comments_count} ${post.comments_count === 1 ? 'comentario' : 'comentarios'}`
              : `View ${post.comments_count} ${post.comments_count === 1 ? 'comment' : 'comments'}`}
          </button>
        )}

        {showComments && (
          <div className="space-y-2 pt-1" data-testid={`post-comments-${post.id}`}>
            {comments === null && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            {comments?.map((c) => (
              <p key={c.id} className="text-sm">
                <button type="button" onClick={() => onOpenProfile?.(c.user_id)} className="font-semibold mr-1.5 hover:underline" data-testid={`comment-author-${c.id}`}>{c.author_name}</button>
                <MentionText text={c.text} onMention={openMention} />
              </p>
            ))}
            <CommentComposer language={language} testIdPrefix={`post-comment-${post.id}`} onSubmit={submitCommentText} />
          </div>
        )}
      </div>

      <Dialog open={reportOpen} onOpenChange={(v) => { if (!v) setReportOpen(false); }}>
        <DialogContent className="max-w-sm" data-testid={`report-dialog-${post.id}`}>
          <DialogTitle className="flex items-center gap-2"><Flag className="w-4 h-4 text-rose-500" /> {es ? 'Reportar publicación' : 'Report post'}</DialogTitle>
          <DialogDescription>{es ? '¿Por qué reportas esto? Nuestro equipo lo revisará.' : 'Why are you reporting this? Our team will review it.'}</DialogDescription>
          <div className="space-y-2 py-1">
            {REPORT_REASONS.map((r) => (
              <button
                key={r.id}
                onClick={() => setReportReason(r.id)}
                className={`w-full text-left text-sm px-3 py-2 rounded-lg border transition-colors ${reportReason === r.id ? 'border-rose-500 bg-rose-50 dark:bg-rose-950/30' : 'border-border hover:bg-muted'}`}
                data-testid={`report-reason-${r.id}`}
              >
                {es ? r.es : r.en}
              </button>
            ))}
            <Textarea
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value)}
              placeholder={es ? 'Detalles (opcional)…' : 'Details (optional)…'}
              rows={2}
              data-testid="report-note"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReportOpen(false)} data-testid="report-cancel">{es ? 'Cancelar' : 'Cancel'}</Button>
            <Button onClick={submitReport} disabled={reporting} className="bg-rose-600 hover:bg-rose-700" data-testid="report-submit">
              {reporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {es ? 'Reportar' : 'Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={(v) => { if (!v) setEditOpen(false); }}>
        <DialogContent className="max-w-sm" data-testid={`edit-dialog-${post.id}`}>
          <DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4" /> {es ? 'Editar publicación' : 'Edit post'}</DialogTitle>
          <DialogDescription>{es ? 'Actualiza el texto de tu publicación.' : 'Update your post caption.'}</DialogDescription>
          <Textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            placeholder={es ? 'Escribe un texto…' : 'Write a caption…'}
            rows={4}
            maxLength={2000}
            data-testid={`edit-caption-${post.id}`}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)} data-testid={`edit-cancel-${post.id}`}>{es ? 'Cancelar' : 'Cancel'}</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid={`edit-save-${post.id}`}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {es ? 'Guardar' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={(v) => { if (!v) setDeleteOpen(false); }}>
        <DialogContent className="max-w-sm" data-testid={`delete-dialog-${post.id}`}>
          <DialogTitle className="flex items-center gap-2"><Trash2 className="w-4 h-4 text-rose-500" /> {es ? 'Eliminar publicación' : 'Delete post'}</DialogTitle>
          <DialogDescription>{es ? 'Esta acción no se puede deshacer. ¿Eliminar esta publicación?' : "This can't be undone. Delete this post?"}</DialogDescription>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)} data-testid={`delete-cancel-${post.id}`}>{es ? 'Cancelar' : 'Cancel'}</Button>
            <Button onClick={confirmDelete} disabled={deleting} className="bg-rose-600 hover:bg-rose-700" data-testid={`delete-confirm-${post.id}`}>
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {es ? 'Eliminar' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PostCard;
